import { FC, ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks";
// import { CustomerSearchMode } from '../../../enum/CustomerSearchMode';
import axios from "axios";
import { Colors, GET_BILL_BY_INVOICE_NUMBER, GET_CUSTOMER_BY_NFC_TAG, GET_CUSTOMER_BILLS_BY_ID, SEARCH_CUSTOMER } from "../../../configs";
import { ICustomerBill } from "../../../typings/interfaces/responses/customer-bills-response";
import { format } from "date-fns";
import { formatDate } from "../../../util/common-functions";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonOffIcon from "@mui/icons-material/PersonOffOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";
import Tooltip from "@mui/material/Tooltip";

import { logoutAction } from "../../../redux/actions/authAction";
import { CustomButton, CustomTextBox, PosLayout, StatusFilter } from "../../../components";
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
} from "@mui/material";
import TopRightButton from "../../../components/common/top-right-button/top-right-button";
import ConfirmDialog from "../../../components/atoms/confirm-box/confirm-box";
import { useTranslation } from "../../../util/translation-provider";
import { ICustomerResponse } from "../../../typings";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import useBarcodeScanner from "../../../hooks/useBarcodeScanner";
import useNfcReader from "../../../hooks/useNfcReader";
import LoaderModal from "../../../components/atoms/loader-modal/loader-modal";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";
import InactivePopupModal from "./InactivePopupModal";
import NfcScanResultModal from "./NfcScanResultModal";
import CustomerTypeModal from "./CustomerTypeModal";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

import {
  LocalShipping,
  LockOutlined as LockIcons,
  Storefront,
  Business as BusinessIcon,
  PersonOutline as EmployeeIcon,
  PermIdentity as RetailIcon,
} from "@mui/icons-material";
import IOSSwitch from "../../../components/atoms/custom-toggle-button/custom-toggle-button";

import { set } from "lodash";
import { primaryAlpha } from "../../../configs/colors";

interface ICustomerResponseWithStatus extends ICustomerResponse {
  status?: "ACTIVE" | "INACTIVE" | "LOCKED";
  isLocked?: boolean;
}

const formatPhoneNumber = (raw: string | null | undefined): string => {
  if (!raw) return "";
  // keep only digits
  let digits = raw.replace(/\D/g, "");

  // strip leading country code 1
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  // only format if we have exactly 10 digits
  if (digits.length !== 10) return raw;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const calculateCustomerStatus = (
  customer: ICustomerResponse
): "ACTIVE" | "INACTIVE" | "LOCKED" => {
  if (customer.accountStatus) {
    return customer.accountStatus;
  }

  const currentDate = new Date();

  if (!customer.lastModifiedTime) {
    return "INACTIVE";
  }

  const lastModifiedDate = new Date(customer.lastModifiedTime);

  const timeDifference = currentDate.getTime() - lastModifiedDate.getTime();

  const daysDifference = timeDifference / (1000 * 60 * 60 * 24);

  if (daysDifference > 365) {
    return "INACTIVE";
  }
  return "ACTIVE";
};

type Activity = "ACTIVE" | "INACTIVE" | "LOCKED";
type CustType = "RETAIL" | "EMPLOYEE" | "BUSINESS";

const ALL_TOOLTIP = `This feature searches the entire customer database and identifies any accounts that have been inactive for two years or more. If you choose to reinstate an inactive account, the customer’s information must first be verified for accuracy, and manager authorization will be required before the account can be reactivated.`;

export const MergedCustomerBadge = ({
  type,
  activity,
  delivery,
  align,
  customer,
}: {
  type?: CustType;
  activity?: Activity;
  delivery?: boolean;
  align?: string;
  customer?: ICustomerResponse;
}) => {
  const boxSize = align === "true" ? 14 : 17;
  const iconSize = align === "true" ? "1rem" : "1.4rem";

  const COLORS = {
    activeRetail: customer?.accountStatus === "ACTIVE" ? "#2E7D32" : Colors.ACCENT_GRAY,
    activeEmployee: customer?.accountStatus === "ACTIVE" ? "#1976D2" : Colors.ACCENT_GRAY,
    inactive: Colors.ACCENT_GRAY,
    lock: "#FFBF00",
    activeBusiness: customer?.accountStatus === "ACTIVE" ? Colors.SECONDARY : Colors.ACCENT_GRAY,
    delivery: customer?.accountStatus === "ACTIVE" ? "#FFBF00" : Colors.ACCENT_GRAY,
  };

  let bg = COLORS.activeBusiness;
  let Icon: typeof RetailIcon | typeof BusinessIcon | typeof EmployeeIcon =
    RetailIcon;
  let title = "";

  if (activity === "LOCKED") {
    title = "Account Status: Locked";
    return (
      <>
        {delivery && (
          <DeliveryBadge
            top={align === "true" ? "10.35rem" : undefined}
            right={align === "true" ? "1rem" : undefined}
            size={boxSize}
            iconSize={iconSize}
            color={COLORS.delivery}
            position={align !== "true" ? "relative" : undefined}
          />
        )}
        <Tooltip arrow placement="top" enterDelay={300} title={title}>
          <span>
            <Badge
              title={title}
              top={align === "true" ? "10.35rem" : undefined}
              right={align === "true" ? "0.5rem" : undefined}
              size={boxSize}
              iconSize={iconSize}
              bg={COLORS.lock}
              position={align !== "true" ? "relative" : undefined}
            >
              <LockIcon sx={{ color: "#fff", fontSize: iconSize }} />
            </Badge>
          </span>
        </Tooltip>
      </>
    );
  }

  if (activity === "INACTIVE") {
    bg = COLORS.inactive;
    Icon =
      type === "BUSINESS"
        ? BusinessIcon
        : type === "EMPLOYEE"
          ? EmployeeIcon
          : RetailIcon;

    title =
      type === "BUSINESS"
        ? "Business Inactive"
        : type === "EMPLOYEE"
          ? "Employee Inactive"
          : "Retail Inactive";

    return (
      <>
        {delivery && (
          <DeliveryBadge
            top={align === "true" ? "10.35rem" : undefined}
            right={align === "true" ? "0.5rem" : undefined}
            size={boxSize}
            iconSize={iconSize}
            color={customer?.accountStatus === "ACTIVE" ? COLORS.delivery : Colors.ACCENT_GRAY}
            position={align !== "true" ? "relative" : undefined}
          />
        )}
        <Badge
          title={title}
          top={align === "true" ? "10.35rem" : undefined}
          right={align === "true" ? "0.5rem" : undefined}
          size={boxSize}
          iconSize={iconSize}
          bg={bg}
          position={align !== "true" ? "relative" : undefined}
        >
          <Icon sx={{ color: "#fff", fontSize: iconSize }} />
        </Badge>
      </>
    );
  }

  if (type === "RETAIL") {
    bg = COLORS.activeRetail;
    Icon = RetailIcon;
    title = "Retail Active";
  } else if (type === "EMPLOYEE") {
    bg = COLORS.activeEmployee;
    Icon = EmployeeIcon;
    title = "Employee Active";
  } else {
    bg = COLORS.activeBusiness;
    Icon = BusinessIcon;
    title = "Business Active";
  }

  return (
    <>
      {delivery && (
        <DeliveryBadge
          top={align === "true" ? "10.35rem" : undefined}
          right={align === "true" ? "0.5rem" : undefined}
          size={boxSize}
          iconSize={iconSize}
          color={COLORS.delivery}
          position={align !== "true" ? "relative" : undefined}
        />
      )}
      <Badge
        title={title}
        top={align === "true" ? "10.35rem" : undefined}
        right={align === "true" ? "0.5rem" : undefined}
        size={boxSize}
        iconSize={iconSize}
        bg={bg}
        position={align !== "true" ? "relative" : undefined}
      >
        <Icon sx={{ color: "#fff", fontSize: iconSize }} />
      </Badge>
    </>
  );
};

const Badge = ({
  children,
  title,
  top,
  right,
  size,
  iconSize,
  bg,
  position = "absolute",
}: {
  children: React.ReactNode;
  title: string;
  top?: string;
  right?: string;
  size: number;
  iconSize: string;
  bg: string;
  position?: "absolute" | "relative" | "static";
}) => (
  <Box
    sx={{
      position,
      top,
      right,
      zIndex: 1,
      backgroundColor: "rgba(255,255,255,0.9)",
      borderRadius: "50%",
      padding: "0.25rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
    title={title}
  >
    <Box
      sx={{
        backgroundColor: bg,
        borderRadius: "4px",
        padding: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      {children}
    </Box>
  </Box>
);

const DeliveryBadge = ({
  top,
  right,
  size,
  iconSize,
  color,
  position,
}: {
  top?: string;
  right?: string;
  size: number;
  iconSize: string;
  color: string;
  position?: "absolute" | "relative" | "static";
}) => (
  <Badge
    title="Delivery: Enabled"
    top={top}
    right={right}
    size={size}
    iconSize={iconSize}
    bg={color}
    position={position}
  >
    <LocalShipping sx={{ color: "#fff", fontSize: iconSize }} />
  </Badge>
);

const PaymentTypeBadge = ({
  type,
  top,
  right,
  size,
  iconSize,
  position,
  customer,
}: {
  type: string;
  top?: string;
  right?: string;
  size: number;
  iconSize: string;
  position?: "absolute" | "relative" | "static";
  customer?: ICustomerResponse;
}) => {
  let bg = Colors.SECONDARY;
  let Icon = CreditCardIcon;
  let title = "";

  if (type === "Monthly Charge") {
    bg = customer?.accountStatus === "ACTIVE" ? "#9C27B0" : Colors.ACCENT_GRAY; // Purple for Monthly
    Icon = CalendarTodayIcon;
    title = "Payment: Monthly Charge";
  } else if (type === "Card on File") {
    bg = customer?.accountStatus === "ACTIVE" ? "#E91E63" : Colors.ACCENT_GRAY; // Pink for Card on File
    Icon = CreditCardIcon;
    title = "Payment: Card on File";
  } else {
    return null;
  }

  return (
    <Badge
      title={title}
      top={top}
      right={right}
      size={size}
      iconSize={iconSize}
      bg={bg}
      position={position}
    >
      <Icon sx={{ color: "#fff", fontSize: iconSize }} />
    </Badge>
  );
};

const SearchResultCad = (props: {
  details: ICustomerResponseWithStatus;
  id: string;
  isFocused: boolean | false;
  onNeedManagerAccess?: (customer: ICustomerResponseWithStatus) => void;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const customerStatus = calculateCustomerStatus(props.details);

  const handleOnItemClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (customerStatus === "ACTIVE") {
      props.details.id && localStorage.removeItem(props.details.id.toString());
      dispatch(CartSliceAction.setSelectedCustomer(props.details));
      navigate(`/pos/${props.details.id}`);
      return;
    }

    if (customerStatus === "INACTIVE" || customerStatus === "LOCKED") {
      props.onNeedManagerAccess?.(props.details);
      return;
    }
  };

  return (
    <div
      id={props.id}
      onClick={handleOnItemClick}
      style={{
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
      }}
    >
      <Box
        sx={{
          background: Colors.WHITE,
          border: props.isFocused
            ? `1px solid ${Colors.SECONDARY}`
            : "1px solid #E6E6E6",
          borderRadius: "0.857rem",
          padding: "0.9rem 1rem",
          mt: "0.75rem",
          boxShadow: "0 0.285rem 0.857rem rgba(0, 0, 0, 0.08)",
          position: "relative",
          cursor: "pointer",
          "&:hover": {
            //backgroundColor: '#f5f5f5',
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "0.5rem",
            right: "0.5rem",
            display: "flex",
            alignItems: "center",
            gap: "0.25rem",
            zIndex: 2,
          }}
        >
          <MergedCustomerBadge
            type={
              props.details.customerTypePref[0] as
              | "RETAIL"
              | "EMPLOYEE"
              | "BUSINESS"
            }
            activity={
              (props.details.customerStatusPref?.[0] ?? "ACTIVE") as Activity
            }
            delivery={props.details.isDeliveryEnabled?.valueOf() ?? false}
            customer={props.details}
          />

          {props.details.paymentType && (
            <PaymentTypeBadge
              type={props.details.paymentType}
              size={17}
              iconSize="1rem"
              position="relative"
              customer={props.details}
            />
          )}
        </Box>

        <Typography
          sx={{
            fontSize: "1.2rem",
            fontWeight: 700,
            color: Colors.TEXT_GRAY_DARK,
          }}
        >
          {props.details.firstName} {props.details.lastName} |{" "}
          {props.details.id}
        </Typography>
        <Typography sx={{ fontSize: "1.14rem", color: Colors.TEXT_GRAY_DARK }}>
          {props.details.notWillingToProvide
            ? "-"
            : [
              props.details.personalAddressLine1,
              props.details.personalAddressLine2,
              props.details.personalCity,
              props.details.personalState,
              props.details.personalZip,
            ]
              .filter((part) => part && part.trim())
              .join(", ")}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: "1.05rem", color: Colors.SECONDARY }}>
            {formatPhoneNumber(props.details.mobilePhone)}
          </Typography>
          <Typography
            sx={{ fontSize: "1.05rem", color: Colors.ACCENT_GRAY_DISABLED }}
          >
            Member since: {props.details.createdTime
              ? formatDate(props.details.createdTime)
              : "N/A"}
          </Typography>
        </Box>
      </Box>
    </div>
  );
};

const options = [
  { label: "Pickup", url: "/", requiredActiveCashier: true },
  { label: "Edit Invoice", url: "/", requiredActiveCashier: true },
  {
    label: "Reprint Invoice",
    url: "/pos/search-reprint",
    requiredActiveCashier: true,
  },
  {
    label: "Reprint",
    url: "/pos/search-reprint",
    requiredActiveCashier: true,
  },
  { label: "Void Invoice", url: "/", requiredActiveCashier: true },
  {
    label: "Conveyor Input",
    url: "/pos/convayor-input",
    requiredActiveCashier: true,
  },
  {
    label: "Cash Register",
    url: "/pos/cash-register",
    requiredActiveCashier: false,
  },
  { label: "Reports", url: "/", requiredActiveCashier: true },
  { label: "Search", url: "/pos/search-general", requiredActiveCashier: true },
  { label: "Retail Sale", url: "/", requiredActiveCashier: true },
  { label: "Price List", url: "/pos/price-list", requiredActiveCashier: true },
  { label: "Pickup & Delivery", url: "/", requiredActiveCashier: true },
  {
    label: "Daily Snapshot",
    url: "/pos/daily-snapshot",
    requiredActiveCashier: true,
  },
  {
    label: "Pin Tag Boxes",
    url: "/pos/pintag-boxes",
    requiredActiveCashier: false,
  },
  { label: "Settings", url: "/pos/settings", requiredActiveCashier: false },
  { label: "Topaz", url: "/pos/topaz-test", requiredActiveCashier: false },
];

const LobbyPage: FC = () => {
  const { showSnackbar } = useSnackbarContext();
  const [searchInProgress, setSearchInProgress] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedText, setDebouncedText] = useState(query);
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const typeFilter = searchParams.get("type") as CustType | null;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const translations = useTranslation();
  const navigate = useNavigate();
  const code = useBarcodeScanner();
  const [ticketLookupInProgress, setTicketLookupInProgress] = useState(false);
  const nfcCard = useNfcReader();
  const [nfcLookupInProgress, setNfcLookupInProgress] = useState(false);
  const [nfcModalOpen, setNfcModalOpen] = useState(false);
  const [nfcCustomer, setNfcCustomer] = useState<ICustomerResponseWithStatus | null>(null);
  const [nfcPendingBills, setNfcPendingBills] = useState<ICustomerBill[]>([]);
  const [focusedIndex, setFocusedIndex] = useState(0);
  type StatusFilterType = "ALL" | "ACTIVE" | "INACTIVE" | "LOCKED";

  const [status, setStatus] = useState<StatusFilterType>("ACTIVE");
  const [isAllMode, setIsAllMode] = useState(false);
  const [customerTypeModalOpen, setCustomerTypeModalOpen] = useState(false);

  const { activeStationCashier } = useAppSelector(
    (state) => state.cashierStation
  );

  const { user } = useAppSelector((state) => state.auth);

  const [searchFilter, setSearchFilter] = useState<
    "phone" | "first" | "last" | "id" | undefined
  >();
  const [rawResults, setRawResults] = useState<ICustomerResponse[]>([]);

  const [searchResults, setSearchResuts] = useState<
    ICustomerResponseWithStatus[]
  >([]);


  const [managerModalOpen, setManagerModalOpen] = useState(false);
  const [selectedCustomerForAccess, setSelectedCustomerForAccess] =
    useState<ICustomerResponseWithStatus | null>(null);

  const [inactivePopupOpen, setInactivePopupOpen] = useState(false);
  const [selectedInactiveCustomer, setSelectedInactiveCustomer] =
    useState<ICustomerResponseWithStatus | null>(null);
  const normalizePhone = (v?: string | null) => (v ?? "").replace(/\D/g, "");
  const WILDCARD = "";

  const isDigitsQuery = (q: string) => {
    const digits = normalizePhone(q);

    return (
      digits.length >= 3 && digits.length >= q.replace(/\s/g, "").length * 0.6
    );
  };

  const handleNeedManagerAccess = (customer: ICustomerResponseWithStatus) => {
    const status = calculateCustomerStatus(customer);

    if (status === "INACTIVE") {
      setSelectedInactiveCustomer(customer);
      setInactivePopupOpen(true);
    } else {
      setSelectedCustomerForAccess(customer);
      setManagerModalOpen(true);
    }
  };

  const handleManagerCodeSuccess = () => {
    if (!selectedCustomerForAccess) return;

    const id = String(selectedCustomerForAccess.id ?? "");
    if (!id) return;

    localStorage.removeItem(id);
    dispatch(CartSliceAction.setSelectedCustomer(selectedCustomerForAccess));

    const status = selectedCustomerForAccess.status ?? "ACTIVE";

    if (status === "LOCKED") {
      showSnackbar(
        "Locked customer account has been blocked due to suspicious behavior",
        "info"
      );
      navigate(`/pos/${id}`);
    } else {
      navigate(`/pos/${id}`);
    }

    setManagerModalOpen(false);
    setSelectedCustomerForAccess(null);
  };

  const handleManagerModalClose = () => {
    setManagerModalOpen(false);
    setSelectedCustomerForAccess(null);
  };

  const handleInactivePopupOk = () => {
    if (!selectedInactiveCustomer) return;

    const id = String(selectedInactiveCustomer.id ?? "");
    if (!id) return;

    localStorage.removeItem(id);
    dispatch(CartSliceAction.setSelectedCustomer(selectedInactiveCustomer));

    navigate(`/pos/customer/${id}/update`, {
      state: { fromInactivePopup: true },
    });

    setInactivePopupOpen(false);
    setSelectedInactiveCustomer(null);
  };

  const handleInactivePopupClose = () => {
    setInactivePopupOpen(false);
    setSelectedInactiveCustomer(null);
  };

  useEffect(() => {
    if (!code) return;

    const invoiceNumber = code.trim();
    if (!invoiceNumber) return;

    setTicketLookupInProgress(true);
    axios
      .get(GET_BILL_BY_INVOICE_NUMBER(invoiceNumber))
      .then((res) => {
        const match = res.data?.body?.content?.[0];
        if (!match) {
          showSnackbar(`No bill found for ${invoiceNumber}`, "error");
          return;
        }
        // customer-history-table.tsx matches this query param against
        // ICustomerBill.billId (the formatted invoice number), not the
        // internal numeric id - same convention handleNfcBillClick uses.
        navigate(
          `/pos/customer/${match.customerId}/history?billId=${match.billId}`
        );
      })
      .catch(() => {
        showSnackbar("Failed to look up scanned bill", "error");
      })
      .finally(() => {
        setTicketLookupInProgress(false);
      });
  }, [code]);

  const handleNfcCheckIn = (customer: ICustomerResponseWithStatus) => {
    setNfcModalOpen(false);
    customer.id && localStorage.removeItem(String(customer.id));
    dispatch(CartSliceAction.setSelectedCustomer(customer));
    navigate(`/pos/${customer.id}`);
  };

  const handleNfcGoToHistory = (customer: ICustomerResponseWithStatus) => {
    setNfcModalOpen(false);
    dispatch(CartSliceAction.setSelectedCustomer(customer));
    navigate(`/pos/customer/${customer.id}/history`);
  };

  const handleNfcBillClick = (customer: ICustomerResponseWithStatus, bill: ICustomerBill) => {
    setNfcModalOpen(false);
    dispatch(CartSliceAction.setSelectedCustomer(customer));
    navigate(`/pos/customer/${customer.id}/history?billId=${bill.billId}`);
  };

  useEffect(() => {
    if (!nfcCard) return;

    setNfcLookupInProgress(true);
    axios
      .get(GET_CUSTOMER_BY_NFC_TAG(nfcCard))
      .then((res) => {
        const customer: ICustomerResponseWithStatus = res.data.body;
        if (!customer) {
          showSnackbar("No customer found for this NFC card", "error");
          return;
        }
        const customerStatus = calculateCustomerStatus(customer);
        if (customerStatus !== "ACTIVE") {
          handleNeedManagerAccess(customer);
          return;
        }
        return axios
          .get(GET_CUSTOMER_BILLS_BY_ID(customer.id!, 50, 0))
          .then((billRes) => {
            const allBills: ICustomerBill[] = billRes.data.body?.content ?? [];
            const pending = allBills.filter(
              (b) =>
                !b.isVoided &&
                !b.isRefunded &&
                b.pickingFilterStatus !== "PICKED_UP"
            );
            setNfcCustomer(customer);
            setNfcPendingBills(pending);
            setNfcModalOpen(true);
          })
          .catch(() => {
            // Bills fetch failed — fall back to direct check-in
            customer.id && localStorage.removeItem(String(customer.id));
            dispatch(CartSliceAction.setSelectedCustomer(customer));
            navigate(`/pos/${customer.id}`);
          });
      })
      .catch(() => {
        showSnackbar("No customer found for this NFC card", "error");
      })
      .finally(() => {
        setNfcLookupInProgress(false);
      });
  }, [nfcCard]);

  const handleOpenConfirm = (openState: boolean) => {
    setConfirmOpen(openState);
  };

  useEffect(() => {
    dispatch(CartSliceAction.clearSession());
  }, []);

  const handleConfirm = () => {
    handleOnLogoutClick();
    handleOpenConfirm(false);
  };

  const onClickMenuItem = (menuItem: string) => {
    switch (menuItem) {
      case "Bags to Rack":
        navigate("/pos/bag-rack-register");
    }
  };

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedText(query);
    }, 800);

    return () => {
      clearTimeout(timerId);
    };
  }, [query]);

  useEffect(() => {
    if (!debouncedText.trim()) {
      setRawResults([]);
      setSearchResuts([]);
      return;
    }

    const shouldFetchBroad =
      searchFilter === "phone" ||
      (!searchFilter && isDigitsQuery(debouncedText));

    const queryForApi = shouldFetchBroad ? WILDCARD : debouncedText;

    setSearchInProgress(true);

    const apiStatus = status === "ALL" || isAllMode ? "" : "ACTIVE";
    axios
      .get(SEARCH_CUSTOMER(queryForApi, apiStatus))
      .then((res) => {
        setRawResults(res.data.body || []);
      })
      .finally(() => setSearchInProgress(false));
  }, [debouncedText, searchFilter, isAllMode, status]); // Added isAllMode dependency

  // Effect to filter results client-side based on 'status' (Left side filter)
  useEffect(() => {
    let results = [...rawResults];

    // Apply Search Filter (Phone/First/Last/ID) - Pre-existing logic
    if (searchFilter) {
      results = results.filter((customer: ICustomerResponse) => {
        switch (searchFilter) {
          case "phone": {
            const q = normalizePhone(debouncedText);
            if (!q) return false;
            return normalizePhone(customer.mobilePhone).includes(q);
          }
          case "first":
            return customer.firstName
              ?.toLowerCase()
              .includes(debouncedText.toLowerCase());
          case "last":
            return customer.lastName
              ?.toLowerCase()
              .includes(debouncedText.toLowerCase());
          case "id":
            return customer.id?.toString().includes(debouncedText);
          default:
            return false;
        }
      });
    } else if (debouncedText.length > 0) {
      // Broad search filter
      results = results.filter((customer: ICustomerResponse) => {
        const searchTerm = debouncedText.toLowerCase();
        const phoneDigits = normalizePhone(customer.mobilePhone);
        const queryDigits = normalizePhone(debouncedText);

        return (
          customer.firstName?.toLowerCase().includes(searchTerm) ||
          customer.lastName?.toLowerCase().includes(searchTerm) ||
          (queryDigits.length > 0 && phoneDigits.includes(queryDigits)) ||
          customer.id?.toString().includes(debouncedText)
        );
      });
    }

    // Calculate status for all results
    const resultsWithStatus: ICustomerResponseWithStatus[] = results.map(
      (customer: ICustomerResponse) => ({
        ...customer,
        status: calculateCustomerStatus(customer),
      })
    );

    // Apply Client-Side Status Filter
    // "if All switch on search all... if switch not switched on fetch only active" (Handled by API)
    // "left side select component... selectbox filteration should work only in client side"
    const filteredByStatus = resultsWithStatus.filter((c) => {
      if (status === "ALL") return true;
      return c.status === status;
    });

    const filteredByType = typeFilter
      ? filteredByStatus.filter((c) =>
          c.customerTypePref?.includes(typeFilter)
        )
      : filteredByStatus;

    setSearchResuts(filteredByType);
  }, [rawResults, status, debouncedText, searchFilter, typeFilter]);

  // Removed old searchCustomers function as it's redundant with useEffects now, or needs to be kept for initial trigger?
  // The original code had both useEffect and searchCustomers. I've consolidated logic into useEffects above.
  // But wait, searchCustomers was called in useEffect. I replaced the API call useEffect. 
  // I should remove the old 'searchCustomers' function definition to avoid confusion if it's not used, 
  // or update it. The original code called 'searchCustomers' inside useEffect[debouncedText, searchFilter].
  // My new useEffect[debouncedText, searchFilter, isAllMode] covers it.

  const onChangeQuery = (data: string) => {
    setQuery(data);
  };

  const handleOnLogoutClick = () => {
    dispatch(logoutAction());
  };

  const handleOnCreateCustomerClick = () => {
    setCustomerTypeModalOpen(true);
  };

  return (
    <PosLayout>
      <Grid
        sx={{
          display: "flex",
          height: "100%",
        }}
      >
        {/* <ConfirmDialog
          open={confirmOpen}
          title="Confirmation"
          message="Are you sure you want to proceed?"
          onCancel={() => handleOpenConfirm(false)}
          onConfirm={handleConfirm}
        /> */}
        <LoaderModal open={ticketLookupInProgress} />
        {nfcLookupInProgress && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(0,0,0,0.35)",
            }}
          >
            <CircularProgress size={56} sx={{ color: "#fff" }} />
          </Box>
        )}
        {nfcCustomer && (
          <NfcScanResultModal
            open={nfcModalOpen}
            customer={nfcCustomer}
            pendingBills={nfcPendingBills}
            onClose={() => setNfcModalOpen(false)}
            onCheckIn={() => handleNfcCheckIn(nfcCustomer)}
            onGoToHistory={() => handleNfcGoToHistory(nfcCustomer)}
            onBillClick={(bill) => handleNfcBillClick(nfcCustomer, bill)}
          />
        )}
        <Grid
          flex={7}
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          {/* Quick Actions header */}
          <Box sx={{ mb: "0.6rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.15em",
                    color: Colors.PRIMARY,
                    lineHeight: 1.3,
                  }}
                >
                  Quick Actions
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.85rem",
                    lineHeight: "2.2rem",
                    color: Colors.TEXT_GRAY_DARK,
                    fontWeight: "600",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Main Lobby
                </Typography>
              </Box>
              <Box
                sx={{
                  px: "0.85rem",
                  py: "0.35rem",
                  borderRadius: "2rem",
                  backgroundColor: "#EEF8F3",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  color: Colors.PRIMARY,
                }}
              >
                {options.length} Shortcuts
              </Box>
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                width: "56%",
                height: "86%",
                overflow: "auto",
                position: "absolute",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
                borderRight: "0.142857143rem solid",
                borderRightColor: Colors.ACCENT_GRAY,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "row",
                  gap: "0.785714286rem",
                  flexWrap: "wrap",
                  pt: "0.75rem",
                  pr: "1.42857143rem",
                }}
              >
                {options?.map((item) => (
                  <Link
                    to={item.url}
                    style={{
                      textDecoration: "none",
                      maxWidth: "calc((100% - (2*0.785714286rem))/3)",
                      minWidth: "calc((100% - (2*0.785714286rem))/3)",
                      flex: 1,
                      maxHeight: "12.1714286rem",
                      alignSelf: "stretch",
                      pointerEvents:
                        item.requiredActiveCashier &&
                          activeStationCashier?.status !== "open"
                          ? "none"
                          : "auto",
                    }}
                  >
                    <CustomButton
                      test-name={`lobby-option-${item.label}`}
                      variant="contained"
                      sx={{
                        width: "100%",
                        height: "100%",
                        boxShadow: `0 8px 20px ${primaryAlpha(0.2)}`,
                        borderRadius: "1.2rem",
                        fontSize: "1.75rem",
                        textTransform: "capitalize",
                        lineHeight: "2.1rem",
                        fontWeight: "600",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 12px 28px ${primaryAlpha(0.28)}`,
                        },
                      }}
                      disabled={
                        item.requiredActiveCashier &&
                        activeStationCashier?.status !== "open"
                      }
                    >
                      {item.label}
                    </CustomButton>
                  </Link>
                ))}
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid flex={5} sx={{ pl: "1.42857143rem", pt: "5.35714286rem" }}>
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: Colors.BACKGROUND_GRAY,
              borderRadius: "0.71rem",
            }}
          >
            <Box
              sx={{
                px: "1.71428571rem",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr auto 1fr",
                  alignItems: "center",
                  pt: "1.25rem",
                  width: "100%",
                  mb: "1.4rem",
                }}
              >
                <Box />
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.15em",
                      color: Colors.SECONDARY,
                      lineHeight: 1.3,
                    }}
                  >
                    Lookup
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "2.1rem",
                      lineHeight: "2.5rem",
                      color: Colors.TEXT_GRAY_DARK,
                      fontWeight: "600",
                    }}
                  >
                    Customer Search
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <CustomButton
                    variant="contained"
                    onClick={handleOnCreateCustomerClick}
                    sx={{
                      width: "3.25rem !important",
                      minWidth: "3.25rem",
                      maxWidth: "3.25rem",
                      height: "3.25rem",
                      borderRadius: "0.75rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      padding: "0",
                      bgcolor: Colors.PRIMARY,
                      "&:hover": { bgcolor: Colors.PRIMARY_DARK },
                    }}
                    disabled={activeStationCashier?.status !== "open"}
                  >
                    <AddIcon sx={{ color: Colors.WHITE }} />
                  </CustomButton>
                </Box>
              </Box>

              <Box sx={{ mb: "1.25rem" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: "0.7rem",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.2rem",
                      fontWeight: 600,
                      color: Colors.TEXT_GRAY_DARK,
                    }}
                  >
                    Search by
                  </Typography>
                  <Tooltip
                    arrow
                    placement="top"
                    enterDelay={400}
                    title={
                      <Box
                        sx={{
                          whiteSpace: "pre-line",
                          maxWidth: 420,
                          fontSize: 10,
                        }}
                      >
                        {ALL_TOOLTIP}
                      </Box>
                    }
                  >
                    <span>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IOSSwitch
                          checked={isAllMode}
                          onChange={(e) => {
                            setIsAllMode(e.target.checked);
                          }}
                          inputProps={{ "aria-label": "All customers" }}
                        />
                        <Typography
                          sx={{ fontSize: "1.1rem", fontWeight: 600, ml: 0.6 }}
                        >
                          All
                        </Typography>
                      </Box>
                    </span>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "nowrap",
                    overflowX: "auto",
                    overflowY: "hidden",
                    columnGap: "0.65rem",
                    pb: 0.5,
                    scrollSnapType: "x proximity",
                    WebkitOverflowScrolling: "touch",

                    "&::-webkit-scrollbar": { height: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#c7c7c7",
                      borderRadius: 6,
                    },

                    "& > *": {
                      flex: "0 0 auto",
                      scrollSnapAlign: "start",
                    },
                  }}
                >
                  {[
                    { key: "phone", label: "Phone Number" },
                    { key: "first", label: "First Name" },
                    { key: "last", label: "Last Name" },
                    { key: "id", label: "Customer ID" },
                  ].map((btn) => (
                    <CustomButton
                      key={btn.key}
                      onClick={() =>
                        setSearchFilter(
                          searchFilter === btn.key
                            ? undefined
                            : (btn.key as any)
                        )
                      }
                      sx={{
                        minWidth: "9rem",
                        height: "2.6rem",
                        borderRadius: "0.6rem",
                        px: "1rem",
                        backgroundColor:
                          searchFilter === btn.key
                            ? Colors.PRIMARY
                            : "#2D8ACF",
                        fontSize: "1rem",
                        fontWeight: 600,
                        textTransform: "none",
                        color: Colors.WHITE,
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor:
                            searchFilter === btn.key
                              ? Colors.PRIMARY_DARK
                              : "#267FBD",
                        },
                      }}
                    >
                      {btn.label}
                    </CustomButton>
                  ))}
                </Box>
              </Box>

              <Box sx={{ mb: "1.25rem" }}>
                <CustomTextBox
                  autoComplete="off"
                  name="search_input"
                  styles={{
                    borderRadius: "10px",
                    height: "3.8rem",
                    backgroundColor:
                      activeStationCashier?.status !== "open"
                        ? Colors.BACKSHADE_GRAY
                        : Colors.WHITE,
                    fontSize: "1.1rem",
                    border: "1.5px solid #E5E7EB",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      fontSize: "1.1rem",
                      "& fieldset": {
                        border: "none",
                      },
                    },
                    "& input::placeholder": {
                      fontSize: "1.05rem",
                      opacity: 0.6,
                    },
                  }}
                  placeholder="Enter Name, Phone Number, Customer ID"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setFocusedIndex(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setFocusedIndex((prev) =>
                        Math.min(prev + 1, searchResults.length - 1)
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setFocusedIndex((prev) => Math.max(prev - 1, 0));
                    } else if (e.key === "Enter") {
                      e.preventDefault();
                      const targetId = `search-result-${focusedIndex}`;
                      document.getElementById(targetId)?.click();
                    }
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: "1rem" }}>
                  <StatusFilter
                    value={status}
                    onChange={(val) => setStatus(val as StatusFilterType)}
                  />
                </Box>
              </Box>

              <Box sx={{ flex: 1, position: "relative" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    overflow: "auto",
                    position: "absolute",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    "-ms-overflow-style": "none",
                    "scrollbar-width": "none",
                  }}
                >
                  {searchInProgress && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "5rem",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}

                  {!searchInProgress &&
                    searchResults.length === 0 &&
                    debouncedText.trim() && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "5rem",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "1.5rem",
                            color: Colors.TEXT_GRAY_DARK,
                          }}
                        >
                          No customers found
                        </Typography>
                      </Box>
                    )}

                  {searchResults.map((result, index) => (
                    <SearchResultCad
                      key={index}
                      //key={`${result.id}-${index}`}
                      details={result}
                      id={`search-result-${index}`}
                      isFocused={index === focusedIndex}
                      onNeedManagerAccess={handleNeedManagerAccess}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
          <CustomerTypeModal
            open={customerTypeModalOpen}
            onClose={() => setCustomerTypeModalOpen(false)}
            onSelectRetail={() => {
              setCustomerTypeModalOpen(false);
              navigate("/pos/customer/create");
            }}
            onSelectBusiness={() => {}}
          />

          <ManagerCodeModal
            open={managerModalOpen}
            onClose={handleManagerModalClose}
            title="Access Required"
            task="access locked customer account"
            onDone={handleManagerCodeSuccess}
            actionType="CUSTOMER_ACCOUNT_ACCESS"
            requestedByUserId={user?.id}
          />
          <InactivePopupModal
            open={inactivePopupOpen}
            onClose={handleInactivePopupClose}
            onOk={handleInactivePopupOk}
          />
        </Grid>
      </Grid>
    </PosLayout>
  );
};

export { LobbyPage };

function showSnackbar(arg0: string, arg1: string) {
  throw new Error("Function not implemented.");
}
